import Component from '@glimmer/component';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { VStack, Section } from 'tio-reimagined/components/layout';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import { formatCurrency } from 'tio-reimagined/format';
import Button from 'tio-reimagined/components/button';
import { t } from 'ember-intl';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import StatePill from 'tio-employee/components/reimagined/tas/state-pill';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type { DataSourceInfo, LiveTasData } from 'tio-employee/services/reimagined-data';

interface TasApplicationHistoryModel {
  tasData: LiveTasData;
  tasUsedAmount: { yearToDate: number; isLiveData: boolean };
  dataSourceInfo: DataSourceInfo;
  applicationId: string;
}

interface TasApplicationHistorySignature {
  Args: {
    model: TasApplicationHistoryModel;
  };
}

export default class CareerPathwaysTasApplicationHistory extends Component<TasApplicationHistorySignature> {
  @service declare reimaginedData: ReimaginedDataService;

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get tasSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.args.model.tasData.isLiveData ? 'live' : 'placeholder';
  }

  get application() {
    return this.args.model.tasData.applications.find(
      (application) => application.id === this.args.model.applicationId
    );
  }

  <template>
    <VStack class="gap-6">
      <div class="tio-col-tight">
        <Heading @level={{1}}>{{t "common.history"}}</Heading>
        <Text @variant="muted">{{t "common.applications"}}</Text>
      </div>

      <DataIndicator
        @source={{this.tasSource}}
        @showBadge={{true}}
        @dataSourceId="tas-application-history"
      >
        <Section>
          <:header>
            <Heading @level={{2}}>{{t "common.history"}}</Heading>
          </:header>
          <:body>
            {{#if this.application}}
              <div class="flex items-start justify-between gap-4">
                <div>
                  <Heading @level={{3}}>{{this.application.name}}</Heading>
                  {{#if this.application.programName}}
                    <Text @variant="muted">{{this.application.programName}}</Text>
                  {{/if}}
                  {{#if this.application.coursesBeginDate}}
                    <Text @variant="muted">
                      {{t
                        "tuition_assistance.reimagined.applications.label_starts"
                        date=this.application.coursesBeginDate
                      }}
                    </Text>
                  {{/if}}
                </div>
                <div class="tio-row-tight">
                  <StatePill @state={{this.application.state}} />
                  <StatePill @state={{this.application.paymentState}} />
                </div>
              </div>
              <div class="mt-4 tio-row-loose">
                <Text @variant="muted">
                  {{t
                    "tuition_assistance.reimagined.applications.label_approved"
                    amount=(formatCurrency this.application.approvedTotal)
                  }}
                </Text>
              </div>
              <div class="mt-4">
                <LinkTo
                  @route="authenticated.tas.applications.history"
                  @model={{@model.applicationId}}
                >
                  <Button @intent="primary">{{t "common.view"}}</Button>
                </LinkTo>
              </div>
            {{else}}
              <Text @variant="muted">{{t
                  "tuition_assistance.reimagined.applications.empty_body"
                }}</Text>
            {{/if}}
          </:body>
        </Section>
      </DataIndicator>
    </VStack>
  </template>
}
